import { Modal } from "components";
import { ButtonTertiary } from "components/Atoms/Button";
import { EditSectionTemplate } from "components/MainSectionTemplate/EditSectionTemplate";
import { SomethingUnexpectedHappenedMessageBox } from "components/MessageBox/SomethingUnexpectedHappenedMessageBox";
import { BookkeepingSmallDetails, CompanySmallDetails } from "components/SmallDetails";
import InvoiceExportMaskDropdown from "components/dropdowns/reactselect/InvoiceExportMaskDropdown";
import { Loading } from "components/panels";
import { useMutation } from "@tanstack/react-query";
import React, { useEffect } from "react";
import { Message } from "components/Atoms/Message";
import { useCustomQuery } from "utilities/hookutils";
import SmallDetails from "components/SmallDetails/SmallDetails";

interface Props {
  onClose: (changed: boolean) => void
  bookkeepingId: number;
}

export const BookkeepingConfigureModal = (props: Props) => {

  const bookkeepingQuery = useCustomQuery({
    queryFn: () => window.CoreApi.Bookkeeping.GetSidePanelData(props.bookkeepingId).then(r => r.data),
    queryKey: ["bookkeepingSidePanelData", props.bookkeepingId],
  });

  const [exportMask, setExportMask] = React.useState<server.dto.ExportMask>(null);

  useEffect(() => {
    if (bookkeepingQuery.data != null) {
      const mask = serverReference.exportMasks.find(m => m.value === bookkeepingQuery.data.data.bookkeeping.exportMask);
      setExportMask(mask);
    }
  }, [bookkeepingQuery.data])


  const saveConfiguration = async () => {

    const dto: server.dto.ConfigureBookkeepingForAdminOneInstruction = {
      bookkeepingId: props.bookkeepingId,
      exportMask: exportMask.value
    }

    return window.CoreApi.Bookkeeping.ConfigureBookkeepingForAdminOne(dto);
  }

  if (bookkeepingQuery.isLoading || bookkeepingQuery.isError) {
    return <Modal
      isOpen
      handleClose={() => props.onClose(false)}
      title="Configure Bookkeeping"
      width="small"
    >
      {bookkeepingQuery.isLoading && <Loading />}
      {bookkeepingQuery.isError && <SomethingUnexpectedHappenedMessageBox />}
    </Modal>
  }

  const { bookkeeping, eInvoiceCompliance } = bookkeepingQuery.data.data;

  return <Modal
    isOpen
    handleClose={() => props.onClose(false)}
    title="Configure Bookkeeping"
    width="small"
  >
    <EditSectionTemplate
      noPanels
      sections={[{
        key: "main",
        type: "regular",
        title: "",
        fields: [
          {
            key: "bookkeeping",
            label: RESX.Bookkeeping.resxBookkeeping,
            renderEditNew: () => <div className="main-section-template-fields-padding">
              <BookkeepingSmallDetails
                bookkeeping={bookkeeping}
              />
            </div>
          }, {
            key: "company",
            label: RESX.Company.resxCompany,
            renderEditNew: () => <div className="main-section-template-fields-padding">
              <CompanySmallDetails
                company={bookkeeping.company}
                layout="multiLine"
              />
            </div>
          }, {
            key: "exportMask",
            label: RESX.InvoiceExport.ExportMask,
            renderEditNew: () => <InvoiceExportMaskDropdown
              options={serverReference.exportMasks}
              value={exportMask}
              onChange={exportMask => setExportMask(exportMask)}
            />
          }, {
            key: "einvoice",
            label: "e-Invoice",
            renderEditNew: () => <EInvoiceComplianceMessage
              compliance={eInvoiceCompliance}
              bookkeeping={bookkeeping}
              onRegistered={() => bookkeepingQuery.refetch()}
            />
          }
        ]
      }]}
      submit={saveConfiguration}
      onSuccess={() => props.onClose(true)}
    />
  </Modal>
}


interface ComplianceProps {
  onRegistered: () => void;
  compliance: server.dto.EInvoiceSenderCompliance;
  bookkeeping: server.dto.Bookkeeping;
}
export const EInvoiceComplianceMessage = (props: ComplianceProps) => {
  const registerMutation = useMutation({
    mutationFn: window.CoreApi.Peppol.RegisterBookkeeping,
    onSuccess: props.onRegistered
  });

  switch (props.compliance.value) {
    case "companyInformationMissing":
    case "countryNotSupported":
      return <div className="main-section-template-fields-padding">
        <SmallDetails
          label={props.compliance.message}
          subLabel={props.compliance.messageSublabel}
        />
      </div>
    case "readyForPeppolRegistration": return <div className="flex-column gap">
      <ButtonTertiary
        label="Register"
        disabled={registerMutation.isLoading}
        loading={registerMutation.isLoading}
        onClick={() => {
          registerMutation.mutate({ bookkeepingId: props.bookkeeping.bookkeepingId })
        }}
      />
      {registerMutation.data?.data.hasErrors && <Message type="error">
        {registerMutation.data.data.errors.map((e) => <div key={e.message}>{e.message}</div>)}
      </Message>}
    </div>
    case "isRegistered":
      return <div className="main-section-template-fields-padding">
        <SmallDetails
          label="Registered"
          subLabel={"Recommand.eu " + props.bookkeeping.recommandCompanyId}
        />
      </div>
  }
}